import * as Yup from "yup";

const nanniesSchema = Yup.object().shape({
  kids: Yup.number()
    .required("Please enter number of kids")
    .typeError("Number of kids must be a number")
    .min(1, "At least 1 kid required")
    .max(10, "Maximum 10 kids allowed"),

  // Ages field - handles single or multiple kids
  ages: Yup.string()
    .required("Please enter ages of kids")
    .test("ages-count", function (value) {
      const kidsCount = this.parent.kids;
      if (!kidsCount || kidsCount < 1) {
        return this.createError({ message: "Number of kids are not selected yet" });
      }

      if (!value) return this.createError({ message: "Please enter ages of kids" });
      const agesArray = value.split(",").map((age) => age.trim()).filter((age) => age.length > 0);

      if (kidsCount > 1) {
        if (agesArray.length < kidsCount) {
          return this.createError({
            message: `Please enter ages for all ${kidsCount} kids (you've entered ${agesArray.length})`,
          });
        }
        if (agesArray.length > kidsCount) {
          return this.createError({
            message: `Too many ages entered. You have ${kidsCount} kids but entered ${agesArray.length} ages`,
          });
        }
      } else {
        if (agesArray.length > 1) {
          return this.createError({
            message: "Please enter only one age (you have 1 kid)",
          });
        }
      }

      return true;
    })
    .test("ages-format", function (value) {
      if (!value) return true; // Skip if empty (handled by required)

      const agesArray = value.split(",").map((age) => age.trim()).filter((age) => age.length > 0);

      for (let i = 0; i < agesArray.length; i++) {
        const age = agesArray[i];

        // Allow formats like: "3", "3.5", "3 years", "3.5 years", "18 months", "1.5 months", "10 days"
        const validPatterns = [
          /^\d+(\.\d+)?$/,                                    // Just number: "3" or "3.5"
          /^\d+(\.\d+)?\s*(years?|yrs?|y)$/i,                // Number with years: "3 years", "3.5 years"
          /^\d+(\.\d+)?\s*(months?|mos?|m)$/i,               // Number with months: "18 months", "1.5 months"
          /^\d+(\.\d+)?\s*(days?|d)$/i,                       // Number with days: "10 days", "5.5 days"
        ];

        const isValid = validPatterns.some((pattern) => pattern.test(age));

        if (!isValid) {
          return this.createError({
            message: `Invalid age format "${age}". Use formats like: "3", "3.5", "3 years", "18 months", or "10 days"`,
          });
        }

        // Extract numeric value and validate range
        const numericMatch = age.match(/^(\d+(\.\d+)?)/);
        if (numericMatch) {
          const numericValue = parseFloat(numericMatch[1]);
          const isMonths = /months?|mos?|m$/i.test(age);
          const isDays = /days?|d$/i.test(age);

          if (numericValue === 0) {
            return this.createError({
              message: `Age cannot be 0. Please enter a valid age (e.g. 0.5)`,
            });
          }

          if (isMonths) {
            if (numericValue < 0 || numericValue > 216) {
              // 18 years in months
              return this.createError({
                message: `Age "${age}" is out of valid range (0.1-216 months)`,
              });
            }
          } else if (isDays) {
            if (numericValue < 0 || numericValue > 6570) {
              // 18 years in days
              return this.createError({
                message: `Age "${age}" is out of valid range (0.1-6570 days)`,
              });
            }
          } else {
            if (numericValue < 0 || numericValue > 18) {
              return this.createError({
                message: `Age "${age}" is out of valid range (0.1-18 years)`,
              });
            }
          }
        }
      }

      return true;
    })
    .max(200, "Ages description too long"),

  allergies: Yup.string()
    .required("Please select if allergies are present")
    .oneOf(["Yes", "No"], "Please select a valid option"),

  allergyDetails: Yup.string()
    .when("allergies", {
      is: "Yes",
      then: (schema) =>
        schema
          .required("Please specify the allergies")
          .max(500, "Allergy details must be less than 500 characters")
          .transform((value) => value?.replace(/\s+/g, " ").trim()),
      otherwise: (schema) => schema.notRequired(),
    }),

  diet: Yup.string()
    .required("Please enter diet/menu preferences")
    .max(500, "Diet preferences must be less than 500 characters")
    .transform((value) => value?.replace(/\s+/g, " ").trim()),

  emergency1Name: Yup.string()
    .required("Please enter first emergency contact name")
    .max(50, "Name must be less than 50 characters")
    .matches(/^[a-zA-Z\s]+$/, "Name must contain only letters and spaces")
    .transform((value) => value?.replace(/\s+/g, " ").trim()),

  emergency1Phone: Yup.string()
    .required("Please enter first emergency contact phone number")
    .matches(/^[0-9\s+\-()]+$/, "Please enter a valid phone number")
    .min(10, "Phone number must be at least 10 digits")
    .max(11, "Phone number must be less than 11 digits")
    .transform((value) => value?.replace(/\s+/g, " ").trim()),

  emergency1Relation: Yup.string()
    .required("Please enter first emergency contact relation")
    .max(30, "Relationship must be less than 30 characters")
    .matches(/^[a-zA-Z\s]+$/, "Relationship must contain only letters and spaces")
    .transform((value) => value?.replace(/\s+/g, " ").trim()),

  emergency2Name: Yup.string()
    .max(50, "Name must be less than 50 characters")
    .matches(/^[a-zA-Z\s]*$/, "Name must contain only letters and spaces")
    .transform((value) => value?.replace(/\s+/g, " ").trim())
    .test("different-name", "Emergency contact names cannot be the same", function (value) {
      if (!value) return true; // Not required, so empty is fine
      const emergency1Name = this.parent.emergency1Name;
      return value.toLowerCase() !== emergency1Name?.toLowerCase();
    }),

  emergency2Phone: Yup.string()
    .matches(/^[0-9\s+\-()]*$/, "Please enter a valid phone number")
    .min(10, "Phone number must be at least 10 digits")
    .max(11, "Phone number must be less than 11 digits")
    .transform((value) => value?.replace(/\s+/g, " ").trim())
    .test("different-phone", "Emergency contact phones cannot be the same", function (value) {
      if (!value) return true; // Not required, so empty is fine
      const emergency1Phone = this.parent.emergency1Phone;
      // Remove non-digit characters for comparison
      const cleanPhone1 = emergency1Phone?.replace(/\D/g, "");
      const cleanPhone2 = value?.replace(/\D/g, "");
      return cleanPhone2 !== cleanPhone1;
    }),

  emergency2Relation: Yup.string()
    .max(30, "Relationship must be less than 30 characters")
    .matches(/^[a-zA-Z\s]*$/, "Relationship must contain only letters and spaces")
    .transform((value) => value?.replace(/\s+/g, " ").trim()),

  // From fieldsConfig for nannies
  specialNeeds: Yup.string()
    .required("Please select if special needs assistance is required")
    .oneOf(["Yes", "No"], "Please select a valid option"),
});

export default nanniesSchema;
